import { z } from 'zod/v3';

/**
 * GitLab Project DTO
 */
export const GitLabProjectSchema = z.object({
  id: z.number(),
  name: z.string(),
  nameWithNamespace: z.string(),
  path: z.string(),
  pathWithNamespace: z.string(),
  description: z.string().nullable().optional(),
  defaultBranch: z.string().optional(),
  visibility: z.enum(['private', 'internal', 'public']),
  httpUrlToRepo: z.string().optional(),
  webUrl: z.string(),
  namespace: z.object({
    id: z.number(),
    name: z.string(),
    path: z.string(),
    kind: z.string(),
    fullPath: z.string(),
  }),
  archived: z.boolean().optional(),
  lastActivityAt: z.string().optional(),
});

export type GitLabProjectDTO = z.infer<typeof GitLabProjectSchema>;

/**
 * GitLab Group DTO
 */
export const GitLabGroupSchema = z.object({
  id: z.number(),
  name: z.string(),
  path: z.string(),
  fullPath: z.string(),
  description: z.string().nullable().optional(),
  visibility: z.enum(['private', 'internal', 'public']),
  webUrl: z.string(),
  parentId: z.number().nullable().optional(),
});

export type GitLabGroupDTO = z.infer<typeof GitLabGroupSchema>;

/**
 * GitLab Merge Request DTO
 */
export const GitLabMergeRequestSchema = z.object({
  id: z.number(),
  iid: z.number(),
  projectId: z.number(),
  title: z.string(),
  description: z.string().nullable().optional(),
  state: z.enum(['opened', 'closed', 'locked', 'merged']),
  createdAt: z.string(),
  updatedAt: z.string(),
  mergedAt: z.string().nullable().optional(),
  closedAt: z.string().nullable().optional(),
  targetBranch: z.string(),
  sourceBranch: z.string(),
  author: z.object({
    id: z.number(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  }),
  assignee: z.object({
    id: z.number(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  }).nullable().optional(),
  assignees: z.array(z.object({
    id: z.number(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  })).optional(),
  reviewers: z.array(z.object({
    id: z.number(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  })).optional(),
  webUrl: z.string(),
  draft: z.boolean().optional(),
  workInProgress: z.boolean().optional(),
  mergeStatus: z.string().optional(),
  sha: z.string().optional(),
  mergeCommitSha: z.string().nullable().optional(),
  squashCommitSha: z.string().nullable().optional(),
  userNotesCount: z.number().optional(),
  upvotes: z.number().optional(),
  downvotes: z.number().optional(),
  labels: z.array(z.string()).optional(),
  hasConflicts: z.boolean().optional(),
  blockingDiscussionsResolved: z.boolean().optional(),
});

export type GitLabMergeRequestDTO = z.infer<typeof GitLabMergeRequestSchema>;

/**
 * GitLab Issue DTO
 */
export const GitLabIssueSchema = z.object({
  id: z.number(),
  iid: z.number(),
  projectId: z.number(),
  title: z.string(),
  description: z.string().nullable().optional(),
  state: z.enum(['opened', 'closed']),
  createdAt: z.string(),
  updatedAt: z.string(),
  closedAt: z.string().nullable().optional(),
  author: z.object({
    id: z.number(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  }),
  assignee: z.object({
    id: z.number(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  }).nullable().optional(),
  assignees: z.array(z.object({
    id: z.number(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  })).optional(),
  webUrl: z.string(),
  labels: z.array(z.string()).optional(),
  milestone: z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullable().optional(),
    state: z.string(),
    dueDate: z.string().nullable().optional(),
  }).nullable().optional(),
  userNotesCount: z.number().optional(),
  upvotes: z.number().optional(),
  downvotes: z.number().optional(),
  dueDate: z.string().nullable().optional(),
  confidential: z.boolean().optional(),
  issueType: z.enum(['issue', 'incident', 'test_case']).optional(),
});

export type GitLabIssueDTO = z.infer<typeof GitLabIssueSchema>;

/**
 * GitLab Branch DTO
 */
export const GitLabBranchSchema = z.object({
  name: z.string(),
  commit: z.object({
    id: z.string(),
    shortId: z.string(),
    title: z.string(),
    authorName: z.string(),
    authoredDate: z.string(),
    committerName: z.string(),
    committedDate: z.string(),
    message: z.string(),
  }),
  merged: z.boolean().optional(),
  protected: z.boolean().optional(),
  default: z.boolean().optional(),
  canPush: z.boolean().optional(),
  webUrl: z.string().optional(),
});

export type GitLabBranchDTO = z.infer<typeof GitLabBranchSchema>;

/**
 * GitLab Repository File DTO
 */
export const GitLabRepositoryFileSchema = z.object({
  fileName: z.string(),
  filePath: z.string(),
  size: z.number(),
  encoding: z.string(),
  content: z.string().optional(),
  contentSha256: z.string().optional(),
  ref: z.string(),
  blobId: z.string(),
  commitId: z.string(),
  lastCommitId: z.string(),
});

export type GitLabRepositoryFileDTO = z.infer<typeof GitLabRepositoryFileSchema>;

/**
 * GitLab User DTO
 */
export const GitLabUserSchema = z.object({
  id: z.number(),
  username: z.string(),
  name: z.string(),
  email: z.string().optional(),
  avatarUrl: z.string().optional(),
  webUrl: z.string(),
  state: z.string().optional(),
  isAdmin: z.boolean().optional(),
});

export type GitLabUserDTO = z.infer<typeof GitLabUserSchema>;

/**
 * GitLab Webhook Event Types
 */
export const GitLabWebhookEventSchema = z.enum([
  'push',
  'tag_push',
  'issue',
  'merge_request',
  'wiki_page',
  'pipeline',
  'job',
  'deployment',
  'release',
]);

export type GitLabWebhookEvent = z.infer<typeof GitLabWebhookEventSchema>;